import React, { useCallback, useEffect, useState } from "react";
import Inbox from "./components/Inbox";
import { CommunicationService } from "@propertelligent/client-api";
import Compose from "./components/Compose";
import ShowEmail from "./components/ShowEmail";
import AddGmailAccount from "./components/AddGmailAccount";
import EmptyData from "../../common/EmptyData";
import EmailListingTopHeader from "../../../newComponents/listings/top-listing-headers/email-listing-top-header";
import SimpleSpinner from "../../loader";
import { GetImages } from "../../../utils/GetImages";
import { Background } from "../../GlobalCss/Background";
import { usePermissions } from "../../../hooks/usePermissions";

const PAGE_SIZE = 10;

const Emailview = () => {
  const [activeIndex, setActiveIndex] = useState("Inbox");
  const [refreshtoken, setRefreshToken] = useState<boolean>(false);
  const [emaillist, setEmailList] = useState([]);
  const [threadlist, setThreadList] = useState([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
  const [listLoading, setListLoading] = useState(false);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [emailId, setEmailId] = useState<any>();
  const [searchEmail, setSearchEmail] = useState("");
  const [emailCat, setEmailCat] = useState("all");
  const [query, setQuery] = useState("inbox");
  const [compose, setcompose] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [connectedEmail, setConnectedEmail] = useState("");
  const [emailCounts, setEmailCounts] = useState({
    inbox: 0,
    important: 0,
    sent: 0,
  });

  const { canWrite } = usePermissions();
  const hasWritePermission = canWrite("Email");

  const queryString = `in:${activeIndex.toLowerCase()} subject:${searchEmail.toLowerCase()} is:${emailCat.toLowerCase()}`;

const onGetEmailProfile = useCallback(() => {
    // Check if function exists before calling
    if (typeof CommunicationService.getApiGmailProfile === 'function') {
        CommunicationService.getApiGmailProfile()
            .then((res) => {
                if (res.success) {
                    setConnectedEmail(res.success.emailAddress || "");
                }
            })
            .catch((err) => console.log(err));
    } else {
        console.warn("getApiGmailProfile method not found in CommunicationService SDK");
    }
}, []);

  const onGetEmailList = useCallback((pageToken?: string, append = false) => {
    if (!refreshtoken) return;
    setListLoading(true);
    CommunicationService.getApiGmailListEmailThreads(
      pageToken ?? undefined,
      PAGE_SIZE,
      queryString
    )
      .then((res: any) => {
        const data = res?.success ?? res;
        const threads = data?.emailThreads ?? data?.threads ?? [];
        const nextToken = data?.nextPageToken ?? data?.nextPageToken ?? undefined;
        setThreadList((prev) => (append ? [...prev, ...threads] : threads));
        setNextPageToken(nextToken);
        setHasMoreThreads(!!nextToken);
        setListLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching threads:", error);
        setListLoading(false);
        setHasMoreThreads(false);
      });
  }, [refreshtoken, queryString]);

  const loadMoreEmailThreads = useCallback(() => {
    if (listLoading || !hasMoreThreads) return;
    if (nextPageToken !== undefined && nextPageToken !== null && nextPageToken !== "") {
      onGetEmailList(nextPageToken, true);
    }
  }, [listLoading, hasMoreThreads, nextPageToken, onGetEmailList]);

  const onGetEmailCount = () => {
    CommunicationService.getApiGmailEmailCounts()
      .then((res) => {
        setEmailCounts({
          inbox: res.success.inbox,
          important: res.success.important,
          sent: res.success.sent,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // Initial Load: 
  useEffect(() => {
    onGetEmailCount();
    CommunicationService.getApiGmailHasRefreshToken()
      .then((res) => {
        if (res.success === true) {
          setRefreshToken(true);
        }
      })
      .catch(() => {
        setRefreshToken(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Fetch Email List and Profile on dependency change (first page only)
  useEffect(() => {
    if (refreshtoken) {
      onGetEmailList(undefined, false);
      onGetEmailProfile();
    }
  }, [refreshtoken, searchEmail, activeIndex, emailCat, onGetEmailList, onGetEmailProfile]);

  const filterOptions = [
    { title: "Inbox", count: emailCounts?.inbox },
    { title: "Sent", count: emailCounts?.sent },
    { title: "Important", count: emailCounts?.important },
  ];

  const handleSelectChange = useCallback((value: string) => setEmailCat(value), []);

  const handleDisconnect = () => {
    setLoading(true);
    CommunicationService.deleteApiGmailDeleteGmailSettings()
      .then((res) => {
        if (res.success) {
          setRefreshToken(false);
          setEmailList([]);
          setThreadList([]);
          setConnectedEmail("");
        }
      })
      .catch((error) => {
        console.error("Error disconnecting account:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div className="emailWrapper">
        {loading ? (
          <div className="spinnerContainer">
            <SimpleSpinner />
          </div>
        ) : !refreshtoken ? (
          <AddGmailAccount border={true} />
        ) : (
          <>
            <EmailListingTopHeader
              PageHeading="Emails"
              ButtonName={hasWritePermission ? "Compose Email" : undefined}
              handleSelectChange={handleSelectChange}
              searchTerm={searchEmail}
              setSearchTerm={setSearchEmail}
              filterOptions={filterOptions}
              setActiveTab={setActiveIndex}
              activeTab={activeIndex}
              disabled={compose}
              aggregateValuesFilters={{ Read: 0, Unread: 0 }}
              onDisconnect={handleDisconnect}
              connectedEmail={connectedEmail}
              onClick={() => {
                setEmailId(null);
                setcompose(true);
              }}
              hasWritePermission={hasWritePermission}
            />
            <div className="emailSubContainer">
              <div className="emailLeftSection">
                <Inbox
                  threadlist={threadlist}
                  emaillist={emaillist}
                  setEmailId={setEmailId}
                  emailId={emailId}
                  setcompose={setcompose}
                  listLoading={listLoading}
                  hasMoreThreads={hasMoreThreads}
                  onLoadMore={loadMoreEmailThreads}
                />
              </div>
              <div className="emailRightSection">
                {emailId ? (
                  <ShowEmail
                    emailId={emailId}
                    onGetEmailList={onGetEmailList}
                    setEmailId={setEmailId}
                  />
                ) : compose ? (
                  <Compose
                    compose={compose}
                    setcompose={setcompose}
                    forwardBody={undefined}
                    getEmailMessages={undefined}
                    replyId={undefined}
                    emailId={undefined}
                    setReplyId={undefined}
                    replytype={undefined}
                  />
                ) : (
                  <EmptyData
                    ImageSrc={GetImages.EmptyEmail}
                    mainText={"Select An Item To Read"}
                    imgBackgroundClass={Background.LightBlue.Round.round80}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Emailview;